import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { documentsApi, wordDocumentsApi, wordpressApi } from '../services/api';
import { Search, Plus, Filter, FileText, FileWarning, LayoutTemplate, Calendar, Clock, ArrowUpDown, ArrowUp, ArrowDown, CloudOff, Cloud, AlertCircle, X, Trash2, CloudOff as Unpublish } from 'lucide-react';
import { getStartingModelLabel, normalizeStartingModels } from '../config/startingModels';
import toast from 'react-hot-toast';
import './Dashboard.css';

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' }
];

function Dashboard() {
  const { selectedBlog, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Initialize filters from URL query parameters
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [modelFilter, setModelFilter] = useState(searchParams.get('model') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [modelOptions, setModelOptions] = useState([]);

  // Track if filters have changed to reset pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Sorting state
  const [sortBy, setSortBy] = useState('date'); // 'name' or 'date'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'

  // Delete confirmation modal for published documents
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deletingWordDocumentId, setDeletingWordDocumentId] = useState(null);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (modelFilter) params.set('model', modelFilter);
    if (statusFilter) params.set('status', statusFilter);
    setSearchParams(params, { replace: true });
    // Reset to page 1 when filters change
    setCurrentPage(1);
  }, [search, modelFilter, statusFilter, setSearchParams]);

  const isViewer = user?.blogs?.find(b => b.id === selectedBlog?.id)?.blog_role === 'viewer';
  const canEdit = user?.role === 'super_admin' || !isViewer;

  useEffect(() => {
    if (selectedBlog) {
      loadDocuments();
    }
  }, [selectedBlog, search, modelFilter, statusFilter, currentPage]);

  useEffect(() => {
    let cancelled = false;

    async function loadModelOptions() {
      if (!selectedBlog?.id) {
        setModelOptions([]);
        return;
      }

      try {
        const response = await wordpressApi.getModels(selectedBlog.id);
        if (cancelled) return;

        const options = normalizeStartingModels(response?.data?.models || []);
        setModelOptions(options);
        if (modelFilter && !options.some(option => option.value === modelFilter)) {
          setModelFilter('');
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to load record type filters:', error);
        setModelOptions([]);
        if (modelFilter) {
          setModelFilter('');
        }
      }
    }

    loadModelOptions();
    return () => { cancelled = true; };
  }, [selectedBlog?.id]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const response = await documentsApi.list(selectedBlog.id, {
        search,
        starting_model: modelFilter,
        status: statusFilter,
        page: currentPage,
        limit: 12
      });
      setDocuments(response.data.documents);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Failed to load documents');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDocument = () => {
    navigate('/editor/new');
  };

  const handleOpenDocument = (doc) => {
    navigate(`/editor/${doc.id}`);
  };

  const handleDeleteDocument = async (doc, e) => {
    e.stopPropagation();

    if (doc.is_published) {
      // Show modal with options for published documents
      setDocumentToDelete(doc);
      setDeleteModalOpen(true);
    } else {
      // Simple confirmation for unpublished documents
      if (!confirm(`Are you sure you want to delete "${doc.name}"?`)) return;
      try {
        await documentsApi.delete(doc.id);
        toast.success('Document deleted');
        loadDocuments();
      } catch (error) {
        toast.error('Failed to delete document');
      }
    }
  };

  const handleDeleteWordDocument = async (doc, e) => {
    e.stopPropagation();
    if (!selectedBlog?.id || deletingWordDocumentId !== null) return;
    const confirmed = confirm(
      `Permanently delete "${doc.name}"?\n\n`
      + 'This removes its WordPress publication, every Word revision, signing setup, '
      + 'render output, and private file. This cannot be undone.'
    );
    if (!confirmed) return;

    setDeletingWordDocumentId(doc.id);
    try {
      const response = await wordDocumentsApi.delete(selectedBlog.id, doc.id);
      setDocuments(current => current.filter(item => item.id !== doc.id));
      if (response.data?.cleanupPending) {
        toast('Document deleted; private file cleanup is pending and will retry automatically');
      } else {
        toast.success('Word document and private files deleted');
      }
      await loadDocuments();
    } catch (error) {
      if (error.response?.data?.cleanupPending) {
        toast.error('Document deletion is incomplete; private file cleanup will retry automatically');
      } else {
        toast.error(error.response?.data?.error || 'Failed to delete Word document');
      }
    } finally {
      setDeletingWordDocumentId(null);
    }
  };

  const handleDeleteEverywhere = async () => {
    if (!documentToDelete) return;
    try {
      await documentsApi.delete(documentToDelete.id);
      toast.success('Document deleted from everywhere');
      setDeleteModalOpen(false);
      setDocumentToDelete(null);
      loadDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleUnpublishOnly = async () => {
    if (!documentToDelete) return;
    try {
      await documentsApi.unpublish(documentToDelete.id);
      toast.success('Document unpublished (kept locally)');
      setDeleteModalOpen(false);
      setDocumentToDelete(null);
      loadDocuments();
    } catch (error) {
      toast.error('Failed to unpublish document');
    }
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setDocumentToDelete(null);
  };

  const handleDuplicateDocument = async (doc, e) => {
    e.stopPropagation();
    try {
      await documentsApi.duplicate(doc.id);
      toast.success('Document duplicated');
      loadDocuments();
    } catch (error) {
      toast.error('Failed to duplicate document');
    }
  };

  const handleUnpublishDocument = async (doc, e) => {
    e.stopPropagation();
    if (!confirm(`Are you sure you want to unpublish "${doc.name}"? This will keep the document locally but remove it from WordPress.`)) return;

    try {
      await documentsApi.unpublish(doc.id);
      toast.success('Document unpublished');
      loadDocuments();
    } catch (error) {
      toast.error('Failed to unpublish document');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Check if a document has pending changes (modified since last sync)
  const hasPendingChanges = (doc) => {
    if (!doc.is_published || !doc.last_synced_at) return false;
    const updatedAt = new Date(doc.updated_at).getTime();
    const syncedAt = new Date(doc.last_synced_at).getTime();
    return updatedAt > syncedAt;
  };

  // Handle sort toggle
  const handleSortClick = (field) => {
    if (sortBy === field) {
      // Toggle order if same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Change field, default to appropriate order
      setSortBy(field);
      setSortOrder(field === 'name' ? 'asc' : 'desc');
    }
  };

  // Sort documents
  const sortedDocuments = [...documents].sort((a, b) => {
    if (sortBy === 'name') {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      const comparison = nameA.localeCompare(nameB);
      return sortOrder === 'asc' ? comparison : -comparison;
    } else {
      // Sort by date
      const dateA = new Date(a.updated_at || 0);
      const dateB = new Date(b.updated_at || 0);
      const comparison = dateA - dateB;
      return sortOrder === 'asc' ? comparison : -comparison;
    }
  });

  if (!selectedBlog) {
    return (
      <div className="dashboard-empty">
        <p>Please select a blog to view documents.</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Documents</h1>
        {canEdit && (
          <button className="btn btn-primary" onClick={handleCreateDocument}>
            <Plus size={20} />
            Create Document
          </button>
        )}
      </div>

      <div className="dashboard-filters">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search documents..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <select
            value={modelFilter}
            onChange={(e) => setModelFilter(e.target.value)}
          >
            <option value="">All Record Types</option>
            {modelOptions.map(model => (
              <option key={model.value} value={model.value}>{model.label}</option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <div className="sort-buttons">
            <button
              className={`btn btn-sort ${sortBy === 'name' ? 'active' : ''}`}
              onClick={() => handleSortClick('name')}
              title={`Sort by name ${sortBy === 'name' ? (sortOrder === 'asc' ? '(A-Z)' : '(Z-A)') : ''}`}
            >
              Name
              {sortBy === 'name' && (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
            </button>
            <button
              className={`btn btn-sort ${sortBy === 'date' ? 'active' : ''}`}
              onClick={() => handleSortClick('date')}
              title={`Sort by date ${sortBy === 'date' ? (sortOrder === 'desc' ? '(newest)' : '(oldest)') : ''}`}
            >
              Date
              {sortBy === 'date' && (sortOrder === 'desc' ? <ArrowDown size={14} /> : <ArrowUp size={14} />)}
            </button>
          </div>

          {(search || modelFilter || statusFilter) && (
            <button
              className="btn btn-text"
              onClick={() => {
                setSearch('');
                setModelFilter('');
                setStatusFilter('');
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="dashboard-loading">
          <div className="spinner" />
          <p>Loading documents...</p>
        </div>
      ) : documents.length === 0 ? (
        <div className="dashboard-empty">
          <FileText size={48} />
          <h2>No documents found</h2>
          {search || modelFilter || statusFilter ? (
            <p>Try adjusting your filters</p>
          ) : (
            <>
              <p>Create your first document to get started</p>
              {canEdit && (
                <button className="btn btn-primary" onClick={handleCreateDocument}>
                  <Plus size={20} />
                  Create Document
                </button>
              )}
            </>
          )}
        </div>
      ) : (
        <>
          <div className="documents-grid">
            {sortedDocuments.map(doc => {
              const editorMode = ['canvas', 'word'].includes(doc.editor_mode) ? doc.editor_mode : 'invalid';
              return (
              <div
                key={doc.id}
                className={`document-card document-card--${editorMode}`}
                onClick={() => handleOpenDocument(doc)}
              >
                <div className="document-preview">
                  {doc.thumbnail ? (
                    <img src={doc.thumbnail} alt={doc.name} className="document-thumbnail" />
                  ) : editorMode === 'word' ? (
                    <FileText size={46} className="document-preview__word-icon" aria-hidden="true" />
                  ) : editorMode === 'canvas' ? (
                    <LayoutTemplate size={40} aria-hidden="true" />
                  ) : (
                    <FileWarning size={40} aria-hidden="true" />
                  )}
                </div>
                <div className="document-info">
                  <h3 className="document-name">{doc.name}</h3>
                  <div className="document-meta">
                    <span className={`document-mode document-mode--${editorMode}`}>
                      {editorMode === 'canvas' ? (
                        <><LayoutTemplate size={12} aria-hidden="true" /> Canvas</>
                      ) : editorMode === 'word' ? (
                        <><FileText size={12} aria-hidden="true" /> Word</>
                      ) : (
                        <><FileWarning size={12} aria-hidden="true" /> Invalid mode</>
                      )}
                    </span>
                    <span className="document-model">{getStartingModelLabel(doc.starting_model)}</span>
                    <span className={`document-status status-${doc.is_published ? 'published' : 'draft'}`}>
                      {doc.is_published ? 'Published' : 'Draft'}
                    </span>
                    {hasPendingChanges(doc) && (
                      <span className="document-status status-pending" title="Changes not synced to WordPress">
                        <AlertCircle size={12} />
                        Pending
                      </span>
                    )}
                  </div>
                  <div className="document-dates">
                    <span title="Last modified">
                      <Clock size={14} />
                      {formatDate(doc.updated_at)}
                    </span>
                    {doc.last_synced_at && (
                      <span title="Last synced to WordPress" className="synced-date">
                        <Cloud size={14} />
                        {formatDate(doc.last_synced_at)}
                      </span>
                    )}
                  </div>
                </div>
                {canEdit && editorMode === 'canvas' && (
                  <div className="document-actions">
                    <button
                      className="btn btn-icon"
                      onClick={(e) => handleDuplicateDocument(doc, e)}
                      title="Duplicate"
                    >
                      Copy
                    </button>
                    {doc.is_published ? (
                      <button
                        className="btn btn-icon btn-warning"
                        onClick={(e) => handleUnpublishDocument(doc, e)}
                        title="Unpublish only (keep local copy)"
                      >
                        Unpublish
                      </button>
                    ) : null}
                    <button
                      className="btn btn-icon btn-danger"
                      onClick={(e) => handleDeleteDocument(doc, e)}
                      title="Delete"
                    >
                      Delete
                    </button>
                  </div>
                )}
                {canEdit && editorMode === 'word' && (
                  <div className="document-actions">
                    <button
                      className="btn btn-icon btn-danger"
                      onClick={(e) => handleDeleteWordDocument(doc, e)}
                      title="Permanently delete Word document and private files"
                      disabled={deletingWordDocumentId !== null}
                    >
                      {deletingWordDocumentId === doc.id ? 'Deleting…' : 'Delete'}
                    </button>
                  </div>
                )}
              </div>
              );
            })}
          </div>

          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => p - 1)}
              >
                Previous
              </button>
              <span>Page {currentPage} of {pagination.pages}</span>
              <button
                className="btn btn-secondary"
                disabled={currentPage === pagination.pages}
                onClick={() => setCurrentPage(p => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Modal for Published Documents */}
      {deleteModalOpen && documentToDelete && (
        <div className="delete-modal-overlay" onClick={closeDeleteModal}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-header">
              <h2>Delete Published Document</h2>
              <button className="btn-close" onClick={closeDeleteModal}>
                <X size={20} />
              </button>
            </div>
            <div className="delete-modal-body">
              <p>
                <strong>"{documentToDelete.name}"</strong> is published to WordPress.
              </p>
              <p>What would you like to do?</p>
            </div>
            <div className="delete-modal-actions">
              <button
                className="btn btn-danger delete-everywhere-btn"
                onClick={handleDeleteEverywhere}
              >
                <Trash2 size={18} />
                Delete Everywhere
              </button>
              <button
                className="btn btn-warning unpublish-only-btn"
                onClick={handleUnpublishOnly}
              >
                <CloudOff size={18} />
                Unpublish Only
              </button>
              <button
                className="btn btn-secondary"
                onClick={closeDeleteModal}
              >
                Cancel
              </button>
            </div>
            <div className="delete-modal-info">
              <p><strong>Delete Everywhere:</strong> Removes document from both this app and WordPress.</p>
              <p><strong>Unpublish Only:</strong> Removes from WordPress but keeps local copy.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
